<script setup lang="ts">
import {
  type ComponentInternalInstance,
  computed,
  type CSSProperties,
  nextTick,
  onMounted,
  onUnmounted,
  provide,
  ref,
  useTemplateRef,
  watch,
} from "vue";
import type { TransitionEasing } from "../models/types";

export type BcBurgerMenuProps = {
  alwaysOpened?: boolean;
  boxShadow?: boolean;
  closeEvents?: (keyof DocumentEventMap)[];
  duration?: number;
  easing?: TransitionEasing;
  iconSize?: string;
  /** Specifies custom function on custom defined close event */
  onCloseEvent?: (
    event: DocumentEventMap[keyof DocumentEventMap],
  ) => void | Promise<void> | PromiseLike<void>;
  /** Specifies custom function when menu toggled */
  onToggle?: () => void | Promise<void> | PromiseLike<void>;
  spacerTopHeight?: string;
  width?: string | number;
};

export type BcBurgerMenuEmit = {
  (event: "after-appear", el: HTMLElement): void;
  (event: "after-enter", el: HTMLElement): void;
  (event: "after-leave", el: HTMLElement): void;
  (event: "appear", el: HTMLElement): void;
  (event: "appear-cancelled", el: HTMLElement): void;
  (event: "before-appear", el: HTMLElement): void;
  (event: "before-enter", el: HTMLElement): void;
  (event: "before-leave", el: HTMLElement): void;
  (event: "enter", el: HTMLElement, done: TimerHandler): void;
  (event: "enter-cancelled", el: HTMLElement): void;
  (event: "leave", el: HTMLElement, done: TimerHandler): void;
  (event: "leave-cancelled", el: HTMLElement): void;
  (event: "toggle", burgerMenuOpened: boolean): void;
};

/** Specifies two-way binding (for visibility) for the burger menu */
const modelValue = defineModel<boolean>();

const {
  alwaysOpened = false,
  boxShadow = true,
  closeEvents = ["pointerdown"],
  duration = 600,
  easing = "ease-in-out",
  iconSize = "1.9311em",
  onCloseEvent,
  onToggle,
  spacerTopHeight,
  width = "25%",
} = defineProps<BcBurgerMenuProps>();

const emit = defineEmits<BcBurgerMenuEmit>();

const burgerMenuOpened = ref<boolean>(false);
const name = ref<string>("");

const burgerMenuRef = useTemplateRef("burgerMenuRef");

const burgerMenuItems = ref<ComponentInternalInstance[]>([]);
provide("burgerMenuItems", burgerMenuItems.value);

const menuStyle = computed<CSSProperties>(() => {
  return {
    "--spacer-top-height": spacerTopHeight
      ? `calc(${spacerTopHeight})`
      : `calc(calc(${iconSize} + 11px + 11px) - 40px)`,
    "--icon-size": iconSize,
  };
});

onMounted(() => {
  if (alwaysOpened) {
    burgerMenuOpened.value = true;
  } else {
    closeEvents.forEach((closeEvent) => {
      document.addEventListener(closeEvent, handleCloseEvent);
    });
  }
  if (burgerMenuRef.value) {
    burgerMenuRef.value.addEventListener("click", closeAllContextMenu);
    burgerMenuRef.value.addEventListener("contextmenu", handleContextMenu);
  }
  nextTick(() => {
    name.value = "slide";
    updateMenuDuration();
    updateMenuEasing();
    updateMenuWidth();
  });
});

onUnmounted(() => {
  closeEvents.forEach((closeEvent) => {
    document.removeEventListener(closeEvent, handleCloseEvent);
  });
  if (burgerMenuRef.value) {
    burgerMenuRef.value.removeEventListener("click", closeAllContextMenu);
    burgerMenuRef.value.removeEventListener("contextmenu", handleContextMenu);
  }
});

function beforeAppear(el: Element): void {
  // Emit the event to the parent
  emit("before-appear", el as HTMLElement);
}

function appear(el: Element): void {
  // Emit the event to the parent
  emit("appear", el as HTMLElement);
}

function afterAppear(el: Element): void {
  // Emit the event to the parent
  emit("after-appear", el as HTMLElement);
}

function appearCancelled(el: Element): void {
  // Emit the event to the parent
  emit("appear-cancelled", el as HTMLElement);
}

function beforeEnter(el: Element): void {
  // Emit the event to the parent
  emit("before-enter", el as HTMLElement);
}

function enter(el: Element, done: TimerHandler): void {
  // Emit the event to the parent
  emit("enter", el as HTMLElement, done);
  // Call done() when the transition ends
  // to trigger the @after-enter event.
  window.setTimeout(done, duration);
}

function afterEnter(el: Element): void {
  // Emit the event to the parent
  emit("after-enter", el as HTMLElement);
}

function enterCancelled(el: Element): void {
  // Emit the event to the parent
  emit("enter-cancelled", el as HTMLElement);
}

function beforeLeave(el: Element): void {
  // Emit the event to the parent
  emit("before-leave", el as HTMLElement);
}

function leave(el: Element, done: TimerHandler): void {
  // Emit the event to the parent
  emit("leave", el as HTMLElement, done);
  // Call done() when the transition ends
  // to trigger the @after-leave event.
  // This will also cause v-show
  // to reapply 'display: none'.
  window.setTimeout(done, duration);
}

function afterLeave(el: Element): void {
  // Close all menu items
  closeAllMenuItems();
  // Emit the event to the parent
  emit("after-leave", el as HTMLElement);
}

function leaveCancelled(el: Element): void {
  // Emit the event to the parent
  emit("leave-cancelled", el as HTMLElement);
}

async function handleCloseEvent(
  event: DocumentEventMap[keyof DocumentEventMap],
): Promise<void> {
  if (onCloseEvent) {
    await onCloseEvent(event);
  } else if (
    burgerMenuRef.value &&
    event.target !== burgerMenuRef.value &&
    !burgerMenuRef.value.contains(event.target as Node)
  ) {
    closeAllContextMenu();
    burgerMenuOpened.value = false;
  }
}

function handleContextMenu(event: MouseEvent): void {
  if (!(event.target as Element)?.closest(".bissantzControls.menuItemControl")) {
    event.preventDefault();
    closeAllContextMenu();
  }
}

async function toggle(): Promise<void> {
  if (onToggle) {
    await onToggle();
  } else {
    burgerMenuOpened.value = !burgerMenuOpened.value;
  }
}

function closeAllContextMenu(): void {
  if (burgerMenuItems.value) {
    burgerMenuItems.value.forEach((child) => {
      const menuItem = child.proxy;
      if (menuItem && menuItem.$.exposed && menuItem.$.exposed.showMenu.value) {
        menuItem.$.exposed.showMenu.value = false;
      }
    });
  }
}

function closeAllMenuItems(): void {
  if (burgerMenuItems.value) {
    burgerMenuItems.value.forEach((item) => {
      const menuItem = item.proxy;
      if (
        menuItem &&
        menuItem.$.exposed &&
        menuItem.$.props &&
        menuItem.$slots.component &&
        menuItem.$.props.hideOnClose
      ) {
        menuItem.$.exposed.isOpen.value = false;
      }
    });
  }
}

function updateMenuDuration(): void {
  if (burgerMenuRef.value) {
    duration !== undefined && duration !== null
      ? burgerMenuRef.value.style.setProperty(
          "--burger-menu-duration",
          `${duration / 1000}s`,
        )
      : burgerMenuRef.value.style.setProperty("--burger-menu-duration", `${600 / 1000}s`);
  }
}

function updateMenuEasing(): void {
  if (burgerMenuRef.value) {
    easing !== undefined && easing !== null
      ? burgerMenuRef.value.style.setProperty("--burger-menu-easing", easing)
      : burgerMenuRef.value.style.setProperty("--burger-menu-easing", "ease-in-out");
  }
}

function updateMenuWidth(): void {
  if (burgerMenuRef.value) {
    if (width !== undefined && width !== null) {
      burgerMenuRef.value.style.setProperty(
        "--burger-menu-width",
        typeof width === "number" ? `${width}px` : width.replace(/\s/g, ""),
      );
    } else {
      burgerMenuRef.value.style.setProperty("--burger-menu-width", "25%");
    }
  }
}

watch(
  modelValue,
  () => {
    if (modelValue.value !== undefined && modelValue.value !== burgerMenuOpened.value) {
      burgerMenuOpened.value = modelValue.value;
    }
  },
  { immediate: true },
);

watch(
  burgerMenuOpened,
  () => {
    if (modelValue.value !== undefined && modelValue.value !== burgerMenuOpened.value) {
      modelValue.value = burgerMenuOpened.value;
      if (!alwaysOpened) {
        emit("toggle", burgerMenuOpened.value);
      }
    }
  },
  { immediate: true },
);

watch(() => duration, updateMenuDuration);

watch(() => easing, updateMenuEasing);

watch(() => width, updateMenuWidth);

defineExpose({
  modelValue,
  burgerMenuOpened,
  burgerMenuRef,
  name,
  toggle,
});
</script>

<template>
  <div
    class="bissantzControls burgerMenuControl"
    data-pak="bc-controls-d7"
    ref="burgerMenuRef"
    v-bind:style="menuStyle"
  >
    <transition
      v-bind:name="name"
      v-on:before-appear="beforeAppear"
      v-on:appear="appear"
      v-on:after-appear="afterAppear"
      v-on:appear-cancelled="appearCancelled"
      v-on:before-enter="beforeEnter"
      v-on:enter="enter"
      v-on:after-enter="afterEnter"
      v-on:enter-cancelled="enterCancelled"
      v-on:before-leave="beforeLeave"
      v-on:leave="leave"
      v-on:after-leave="afterLeave"
      v-on:leave-cancelled="leaveCancelled"
    >
      <div v-bind:class="['burgerMenu', { boxShadow }]" v-show="burgerMenuOpened">
        <div class="burgerMenuContent">
          <div class="spacerTop"></div>
          <div class="headerContainer">
            <slot name="header" />
          </div>
          <div class="mainArea">
            <slot name="main-area"></slot>
          </div>
          <div class="spacerBottom">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </transition>
    <div v-if="!alwaysOpened" class="menu">
      <div class="menuButton" v-on:click.self="toggle"></div>
    </div>
  </div>
</template>

<style lang="less">
@import "../styles/components/bc-burger-menu.less";
</style>
